/**
 * Request transport shared by every native-agent provider session.
 *
 * Connection, timeout, and auth-refresh retries stay delegated to
 * `fetchWithRetry`. On top of that this adds three bounded retry rules:
 *
 *  - transient statuses (408, 409, 429, 5xx, 529): up to two retries, waiting
 *    for `retry-after` (capped at 5s) or a short jittered backoff;
 *  - HTTP 400 whose non-JSON body is recognizable HTML from an intermediary:
 *    up to two retries;
 *  - a 200 stream that carries an explicit transient provider error frame, as
 *    classified by the session's `isTransientBodyError`: up to two retries.
 *    `onReplay` lets the session clear the discarded attempt's live UI before
 *    the replay streams again.
 *
 * A JSON 400, any other 4xx, a caller abort, a network failure while reading
 * the body, and a stream that ends without completing are never retried: a
 * cut-off stream may be a client or proxy timeout, and replaying it would
 * multiply an already long wait.
 */

import { diagnoseBlockedResponse, fetchWithRetry, readModelError } from "../../../utils";
import {
  logAiClientEvent,
  type AiClientLogEvent,
} from "../../../../shared/api/finster/aiClientLog";
import { isHtmlErrorBody } from "../../../../shared/utils/apiError";

export type NativeAgentRoute = "/api/ai/openai/responses" | "/api/ai/anthropic/messages";

const CLIENT_LOG_EVENTS = {
  "/api/ai/openai/responses": {
    noResponse: "openai_responses_no_response",
    syntheticHtml400: "openai_responses_synthetic_html_400",
    transientStatusRetry: "openai_responses_transient_status_retry",
    streamFailureRetry: "openai_responses_stream_failure_retry",
  },
  "/api/ai/anthropic/messages": {
    noResponse: "anthropic_messages_no_response",
    syntheticHtml400: "anthropic_messages_synthetic_html_400",
    transientStatusRetry: "anthropic_messages_transient_status_retry",
    streamFailureRetry: "anthropic_messages_stream_failure_retry",
  },
} as const satisfies Record<
  NativeAgentRoute,
  {
    noResponse: AiClientLogEvent;
    syntheticHtml400: AiClientLogEvent;
    transientStatusRetry: AiClientLogEvent;
    streamFailureRetry: AiClientLogEvent;
  }
>;

export const TRANSIENT_STATUS_CODES = new Set([408, 409, 429, 500, 502, 503, 504, 529]);
export const MAX_TRANSIENT_STATUS_RETRIES = 2;
export const MAX_STREAM_FAILURE_RETRIES = 2;
const MAX_RETRY_AFTER_MS = 5_000;
const TRANSIENT_STATUS_BACKOFF_MS = [500, 1_500] as const;

const SYNTHETIC_HTML_400_RETRY_DELAYS_MS = [500, 1_500] as const;
const RETRY_JITTER_MS = 250;
const CF_RAY_PATTERN = /^[a-f0-9]{8,32}(?:-[a-z0-9]{2,10})?$/i;

/** Creates a standard abort error while preserving an Error-valued signal reason. */
function abortError(signal?: AbortSignal): Error {
  if (signal?.reason instanceof Error) return signal.reason;
  const error = new Error("The operation was aborted");
  error.name = "AbortError";
  return error;
}

/** Stops retry processing immediately when the caller has cancelled the model turn. */
function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw abortError(signal);
}

/** Identifies complete JSON bodies so genuine API 400 responses are never retried as edge HTML. */
function isJsonResponseBody(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

/** Maps response-less failures to the bounded vocabulary accepted by the telemetry endpoint. */
function classifyNoResponseFailure(error: unknown, signal?: AbortSignal) {
  if (signal?.aborted || (error instanceof Error && error.name === "AbortError")) {
    return "aborted" as const;
  }

  const message = error instanceof Error ? error.message.toLowerCase() : "";
  if (message.includes("timed out") || message.includes("timeout")) {
    return "timeout" as const;
  }
  if (message === "load failed") return "load_failed" as const;
  if (message.includes("failed to fetch")) return "failed_to_fetch" as const;
  if (message.includes("network")) return "network_error" as const;
  return "other" as const;
}

/** Returns a Cloudflare Ray ID only when it is safe and structurally valid to log. */
function sanitizeCfRay(value: string | null): string | undefined {
  const candidate = value?.trim();
  return candidate && CF_RAY_PATTERN.test(candidate) ? candidate : undefined;
}

/** Creates a stable eight-hex fingerprint for grouping equal bodies without logging their content. */
function fingerprintText(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/** Waits for a retry delay and remains cancellable for the whole wait. */
async function waitBeforeRetry(delayMs: number, signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  await new Promise<void>((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const onAbort = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      reject(abortError(signal));
    };
    timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
  throwIfAborted(signal);
}

function jitter(): number {
  return Math.floor(Math.random() * RETRY_JITTER_MS);
}

function retryAfterMs(response: Response): number | null {
  const header = response.headers?.get?.("retry-after");
  if (!header) return null;
  const seconds = Number.parseFloat(header);
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
}

/** Releases the failed response stream before retrying, without masking the original failure. */
async function cancelResponseBody(response: Response): Promise<void> {
  try {
    await response.body?.cancel();
  } catch {
    // Best effort only; the next retry must not be blocked by cleanup.
  }
}

export interface SendNativeAgentRequestParams<T> {
  route: NativeAgentRoute;
  url: string;
  init: RequestInit;
  requestBytes: number;
  requestLabel: string;
  /** Connection-level retries delegated to `fetchWithRetry`. */
  retries: number;
  signal?: AbortSignal;
  /** Consumes a successful response. */
  readBody: (response: Response) => Promise<T>;
  /** True when a `readBody` failure is a provider-reported stream failure worth replaying. */
  isTransientBodyError?: (error: unknown) => boolean;
  /** Called before a stream failure replay, so live UI from the failed attempt can be cleared. */
  onReplay?: () => void;
}

export async function sendNativeAgentRequest<T>(
  params: SendNativeAgentRequestParams<T>
): Promise<T> {
  const {
    route,
    url,
    init,
    requestBytes,
    requestLabel,
    retries,
    signal,
    readBody,
    isTransientBodyError,
    onReplay,
  } = params;
  const events = CLIENT_LOG_EVENTS[route];
  const maxHtmlAttempts = SYNTHETIC_HTML_400_RETRY_DELAYS_MS.length + 1;
  const maxTotalAttempts =
    maxHtmlAttempts + MAX_TRANSIENT_STATUS_RETRIES + MAX_STREAM_FAILURE_RETRIES;
  let htmlRetries = 0;
  let statusRetries = 0;
  let streamRetries = 0;
  const totalAttempt = () => htmlRetries + statusRetries + streamRetries + 1;

  const logNoResponseFailure = (
    attemptStartedAt: number,
    responseStage: "headers" | "body",
    error: unknown
  ) => {
    logAiClientEvent(events.noResponse, {
      requestLabel,
      attempt: totalAttempt(),
      maxAttempts: maxTotalAttempts,
      requestBytes,
      durationMs: Date.now() - attemptStartedAt,
      signalAborted: signal?.aborted === true,
      responseStage,
      failureKind: classifyNoResponseFailure(error, signal),
    });
  };

  for (;;) {
    const attemptStartedAt = Date.now();
    let response: Response;
    try {
      response = await fetchWithRetry(url, init, retries, requestLabel);
    } catch (error) {
      logNoResponseFailure(attemptStartedAt, "headers", error);
      throw error;
    }

    if (response.ok) {
      try {
        return await readBody(response);
      } catch (error) {
        if (
          isTransientBodyError?.(error) === true &&
          streamRetries < MAX_STREAM_FAILURE_RETRIES &&
          signal?.aborted !== true
        ) {
          const delayMs = TRANSIENT_STATUS_BACKOFF_MS[streamRetries] + jitter();
          logAiClientEvent(events.streamFailureRetry, {
            requestLabel,
            attempt: totalAttempt(),
            maxAttempts: maxTotalAttempts,
            requestBytes,
            durationMs: Date.now() - attemptStartedAt,
            signalAborted: false,
            retryAfterMs: Math.round(delayMs),
          });
          streamRetries += 1;
          onReplay?.();
          await waitBeforeRetry(delayMs, signal);
          continue;
        }
        logNoResponseFailure(attemptStartedAt, "body", error);
        throw error;
      }
    }

    if (
      TRANSIENT_STATUS_CODES.has(response.status) &&
      statusRetries < MAX_TRANSIENT_STATUS_RETRIES &&
      signal?.aborted !== true
    ) {
      const serverDelayMs = retryAfterMs(response);
      const delayMs = serverDelayMs ?? TRANSIENT_STATUS_BACKOFF_MS[statusRetries] + jitter();
      logAiClientEvent(events.transientStatusRetry, {
        requestLabel,
        attempt: totalAttempt(),
        maxAttempts: maxTotalAttempts,
        requestBytes,
        durationMs: Date.now() - attemptStartedAt,
        signalAborted: false,
        status: response.status,
        retryAfterMs: Math.round(delayMs),
      });
      statusRetries += 1;
      await cancelResponseBody(response);
      await waitBeforeRetry(delayMs, signal);
      continue;
    }

    let bodyText = "";
    if (response.status === 400) {
      try {
        bodyText = await response.clone().text();
      } catch (error) {
        logNoResponseFailure(attemptStartedAt, "body", error);
        throw error;
      }
    }
    const diagnosis = response.status === 400 ? diagnoseBlockedResponse(response, bodyText) : null;
    if (
      response.status !== 400 ||
      isJsonResponseBody(bodyText) ||
      !isHtmlErrorBody(bodyText) ||
      diagnosis === null
    ) {
      throw new Error(await readModelError(response, `${requestLabel} request failed`));
    }

    const willRetry = htmlRetries < maxHtmlAttempts - 1 && signal?.aborted !== true;
    const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
    logAiClientEvent(events.syntheticHtml400, {
      requestLabel,
      attempt: htmlRetries + 1,
      maxAttempts: maxHtmlAttempts,
      requestBytes,
      durationMs: Date.now() - attemptStartedAt,
      status: 400,
      blockedBy: diagnosis.source,
      cfRay: sanitizeCfRay(diagnosis.cfRay),
      bodyHash: fingerprintText(bodyText),
      htmlContentType: contentType.startsWith("text/html"),
      cfMitigated: diagnosis.cfMitigated !== null,
      viaPresent: diagnosis.via !== null,
      responseBytes: new TextEncoder().encode(bodyText).byteLength,
      signalAborted: signal?.aborted === true,
      willRetry,
    });

    if (!willRetry) {
      throwIfAborted(signal);
      throw new Error(await readModelError(response, `${requestLabel} request failed`));
    }

    await cancelResponseBody(response);
    await waitBeforeRetry(SYNTHETIC_HTML_400_RETRY_DELAYS_MS[htmlRetries] + jitter(), signal);
    htmlRetries += 1;
  }
}
